import { Helmet } from "react-helmet-async";
import { useEventContext } from "../../context-state/EventContext";
import { useMarketplaceContext } from "../../context-state/MarketplaceContext";
import { Loader } from "../../components/minimal/loading-screen/Loader";
import { useEffect, useState } from "react";
import { EventService } from "../../services/eventService";
import { EventView } from "../common/event/EventView";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnalyticsService } from "../../services/analyticsService";
import { ErrorService } from "../../services/errorService";
import { getPageTitleByDomain, getMarketplaceLoader } from "../../utils/market";
import { isSafariPrivateMode } from "../../utils/browser.js";
import { ErrorBoundary } from "react-error-boundary";
import DynamicError from "../../components/DynamicError";
import { useLocales } from "../../locales";
import { IFrameView } from "../common/event/IFrameView";
import Alert from '@mui/material/Alert';
import { getEndUserByIdPublic } from "../../api/index.js";
import { fetchEndUserData } from "../../services/endUserService";
const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
        img.onerror = reject;
    }).catch(err => {
        console.error('Failed to preload event image')
        return null;
    });
};

const Fallback = () => {
    const { translate } = useLocales()
    // get the current href
    const { href } = window.location

    return (<DynamicError
        title={translate('event_page.error_fallback.title')}
        subtitle={translate('event_page.error_fallback.subtitle')}
        actionBtnMsg={translate('event_page.error_fallback.action_btn_msg')}
        href={href}
    />)
}

const onEventPageError = (error, info) => {
    console.error(error)
    const hint = { type: 'event_page_error', info }
    ErrorService.handleError(error, hint)
}

export default function EventPage() {
    const { translate } = useLocales();
    const navigate = useNavigate();
    const { eventPageContext } = useEventContext()
    const { setMarketplace } = useMarketplaceContext()
    const { event, setEvent, setAccount, setTickets, account } = eventPageContext
    console.info({ event })
    const navigateHome = () => navigate('/', { replace: true });
    const [searchParams] = useSearchParams();
    const utm = searchParams.get('utm')
    const [onSafariPrivateMode, setOnSafariPrivateMode] = useState(false)
    const [fetchingData, setFetchingData] = useState(true)

    const getEndUserDetailsById = async ({ marketplaceId, accountId }) => {
        const endUserId = searchParams.get('endUserId') || localStorage.getItem('endUserId')
        try {
            if (endUserId) {
                const { data } = await getEndUserByIdPublic({ endUserId, marketplaceId, accountId })
                eventPageContext.setInitialUserEventDetails(data)
            }
        } catch (err) {
            console.error(`Failed to get end user by id: ${endUserId}`)
            console.error(err)
        }
    }

    const getEventData = async () => {
        try {
            setFetchingData(true)
            const [, , accountHandler, eventHandler] = window.location.pathname.split('/')
            const { eventData, accountData, ticketsData, marketplaceData } = await EventService.getEventPageData(accountHandler, eventHandler, utm)
            if (!eventData.is_enabled) {
                navigateHome()
                return
            }
            const imagePromise = preloadImage(eventData.design.image)

            setEvent(eventData)
            setAccount(accountData)
            setTickets(ticketsData)
            setMarketplace(marketplaceData)
            getEndUserDetailsById({ marketplaceId: marketplaceData._id, accountId: accountData._id })
            AnalyticsService.incUtm(utm, accountData._id, eventData._id)
            const isLeanEvent = searchParams.get('isLeanEvent') || eventData.isLeanEvent
            if (!isLeanEvent) { // if we are not displaying the image no need to wait for it to load
                await imagePromise
            }
            if (eventData.isOnlyForMembers) {
                const accountIsSeller = accountData.isSubAccount === false
                const seller = accountIsSeller ? accountData : accountData.parent
                const result = await fetchEndUserData(seller, accountData).catch(err => {
                    // fail silently
                    // no need to show no end user means he can't see the event anyway
                    return null
                })
                if (result) {
                    eventPageContext.setEndUser(result.endUser)
                }
            }
        } catch (e) {
            console.log(e);
            ErrorService.handleError(e)
            navigateHome()
        } finally {
            setFetchingData(false)
        }
    }

    // Currently, the PayMe hosted fields dont work on Safari private mode. so we check this and present message to the user
    const detectPrivateModeOnSafari = async () => {
        const isPrivateMode = await isSafariPrivateMode()
        if (isPrivateMode) {
            setOnSafariPrivateMode(true)
        }
    }

    useEffect(() => {
        detectPrivateModeOnSafari()
        if (!event) {
            getEventData()
        }
    }, [])

    const CurrentMarketplaceLoader = getMarketplaceLoader();
    const loader = CurrentMarketplaceLoader ? <CurrentMarketplaceLoader /> : <Loader />
    
    return (
        <>
            <Helmet>
                <title> {getPageTitleByDomain()} | {event?.name || ''} </title>
                <script src="https://cdn.paymeservice.com/hf/v1/hostedfields.js" />
                {event?.font && event?.fontUrl && (
                    <style>
                        {`
                            @font-face {
                                font-family: ${event.font};
                                src: url('${event.fontUrl}');
                                font-weight: normal;
                                font-style: normal;
                            }
                            @font-face {
                                font-family: ${event.font};
                                src: url('${event.fontUrl}');
                                font-weight: bold;
                                font-style: normal;
                            }
                        `}
                    </style>
                )}
                {event?.titleFont && event?.titleFontUrl && (
                    <style>
                        {`
                            @font-face {
                                font-family: ${event.titleFont};
                                src: url('${event.titleFontUrl}');
                                font-weight: normal;
                                font-style: normal;
                            }
                            @font-face {
                                font-family: ${event.titleFont};
                                src: url('${event.titleFontUrl}');
                                font-weight: bold;
                                font-style: normal;
                            }
                        `}
                    </style>
                )}
            </Helmet>
            {onSafariPrivateMode && <Alert onClose={() => setOnSafariPrivateMode(false)} sx={{ position: "fixed", top: 0, right: 0, left: 0, zIndex: 1000 }} severity="error">{translate('event_page.safari_private_mode_message')}</Alert>}
            <ErrorBoundary FallbackComponent={Fallback} onError={onEventPageError}>
                {fetchingData ? <IFrameView>{loader}</IFrameView> : <EventView event={event} account={account} />}
            </ErrorBoundary>
        </>
    )

}
